import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import moment from 'moment';
import styled from 'styled-components';
import {
  DateRangeField,
  FlexColumn,
  FlexMiddle,
  FormModal,
  InputField,
  Label,
  Loader,
  SelectField,
  SmallBody
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { required } from '@ubeya/shared/utils/form';
import {
  LEAVE_ERROR_EMPLOYEE_BOOKED_ON_DATES,
  LEAVE_ERROR_INSUFFICIENT_FUNDS_WILL_GO_NEGATIVE,
  LEAVE_ERROR_WILL_EXCEED_MONTHLY_EARNINGS_LIMIT
} from '@ubeya/shared/constants/leave';
import { EXPECTED_PAY_STATUS_I18N, useExpectedLeavePay } from 'shared/hooks/useExpectedLeavePay';
import LeaveApprovalConfirmModal from '../../../LeaveApprovalConfirmModal';
import LeaveEarningsLimitWarningModal from '../../../LeaveEarningsLimitWarningModal';

import { countEligibleLeaveDays, isMoneyBalance } from '../leaveBalanceUtils';

// DateRangeField surfaces `{ startDate, endDate }` even when half the range is
// empty, so a plain `required` validator passes when only one end is picked.
// Backend rejects: both halves are yup.date().required(). Block the submit early.
const requiredDateRange = (value) => (value?.startDate && value?.endDate ? undefined : 'required');

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const PreviewBox = styled(FlexMiddle)`
  padding: 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grey1};
  gap: 8px;
  min-height: 42px;
`;

const PreviewLabel = styled(SmallBody)`
  color: ${({ theme }) => theme.colors.grey4};
`;

const PreviewValue = styled(SmallBody)`
  font-weight: 600;
`;

const MoneyPotInfoBox = styled(FlexColumn)`
  padding: 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.blueLight};
  color: ${({ theme }) => theme.colors.blue};
  gap: 4px;
`;

const MoneyPotInfoText = styled(SmallBody)`
  color: inherit;
`;

const MoneyPotPreviewStrip = styled(FlexColumn)`
  padding: 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grey1};
  gap: 4px;
`;

const ExcludedDaysNote = ({ count }) => {
  const { t } = useContext(ConfigContext);
  if (!count) return null;
  return <PreviewLabel>{t('employeeLeave_excludedDaysNote', { count })}</PreviewLabel>;
};

const MoneyPotPreview = ({ balance, excludedHolidays, formattingMap }) => {
  const { t } = useContext(ConfigContext);
  const { input: datesInput } = useField('dates', { subscription: { value: true } });
  const { input: durationInput } = useField('durationHoursPerDay', { subscription: { value: true } });
  const { input: branchInput } = useField('branchId', { subscription: { value: true } });

  const avgDayPay = Number(balance?.avgDayPay) || 0;
  const available = Number(balance?.balance) || 0;
  const { eligible: dayCount, excluded: excludedDayCount } = countEligibleLeaveDays(
    datesInput.value,
    balance?.config?.excludedDays,
    excludedHolidays,
    branchInput.value || undefined
  );

  // Use balance.dayHours (BE's avgDayPay denominator) so the debit math matches the server.
  const blockedHours = Number(balance?.dayHours) > 0 ? Number(balance.dayHours) : 8;
  const overrideHours = Number(durationInput.value);
  const hasOverride = Number.isFinite(overrideHours) && overrideHours > 0 && overrideHours !== blockedHours;
  const hourlyRate = blockedHours > 0 ? avgDayPay / blockedHours : 0;
  const perDayPay = hasOverride ? hourlyRate * overrideHours : avgDayPay;
  const debit = perDayPay > 0 ? dayCount * perDayPay : 0;
  const remaining = available - debit;

  const rateLabel = formattingMap?.currency(perDayPay, { includeZero: true }) ?? `${perDayPay.toFixed(2)}`;
  const debitLabel = formattingMap?.currency(debit, { includeZero: true }) ?? `${debit.toFixed(2)}`;
  const remainingLabel = formattingMap?.currency(remaining, { includeZero: true }) ?? `${remaining.toFixed(2)}`;

  return (
    <>
      {avgDayPay > 0 && (
        <MoneyPotInfoBox>
          <MoneyPotInfoText>
            {t('employeeLeave_moneyPot_payRate', {
              rate: rateLabel,
              hours: blockedHours ? Number(blockedHours).toFixed(1) : '8'
            })}
          </MoneyPotInfoText>
        </MoneyPotInfoBox>
      )}
      {dayCount > 0 && avgDayPay > 0 && (
        <MoneyPotPreviewStrip>
          <PreviewValue>
            {t('employeeLeave_moneyPot_debitPreview', {
              count: dayCount,
              rate: rateLabel,
              total: debitLabel
            })}
          </PreviewValue>
          <PreviewLabel>{t('employeeLeave_moneyPot_remainingAfter', { remaining: remainingLabel })}</PreviewLabel>
        </MoneyPotPreviewStrip>
      )}
      <ExcludedDaysNote count={excludedDayCount} />
    </>
  );
};

const ExpectedPayPreview = ({ fetchExpectedPay, formattingMap }) => {
  const { t } = useContext(ConfigContext);
  const { input: leaveInput } = useField('accountContractV2LeaveId', { subscription: { value: true } });
  const { input: datesInput } = useField('dates', { subscription: { value: true } });
  const { input: durationInput } = useField('durationHoursPerDay', { subscription: { value: true } });
  const { input: branchInput } = useField('branchId', { subscription: { value: true } });

  const accountContractV2LeaveId = leaveInput.value;
  const startDate = datesInput.value?.startDate?.format?.(API_DATE_FORMAT);
  const endDate = datesInput.value?.endDate?.format?.(API_DATE_FORMAT);
  const parsedDuration = Number(durationInput.value);
  const durationHours = Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : undefined;

  const { preview, loading, statusKey, hasInputs } = useExpectedLeavePay({
    accountContractV2LeaveId,
    startDate,
    endDate,
    durationHours,
    branchId: branchInput.value || undefined,
    fetchExpectedPay
  });

  if (!hasInputs) return null;

  const dayCount = Number(preview?.dayCount) || 0;
  const excludedDayCount = Number(preview?.excludedDayCount) || 0;
  const dayHours = Number(preview?.dayHours) || 0;
  const hourlyWage = Number(preview?.hourlyWage) || 0;
  const totalPay = Number(preview?.totalPay) || 0;
  const totalLabel = formattingMap?.currency(totalPay, { includeZero: true }) ?? totalPay.toFixed(2);
  const rateLabel = formattingMap?.currency(hourlyWage, { includeZero: true }) ?? hourlyWage.toFixed(2);

  return (
    <PreviewBox>
      {loading ? (
        <Loader size={16} />
      ) : statusKey ? (
        <FlexColumn style={{ gap: 4 }}>
          <PreviewLabel>{t(EXPECTED_PAY_STATUS_I18N[statusKey])}</PreviewLabel>
          <ExcludedDaysNote count={excludedDayCount} />
        </FlexColumn>
      ) : (
        <FlexColumn style={{ gap: 4 }}>
          <FlexMiddle style={{ gap: 8 }}>
            <PreviewLabel>{t('expectedPay')}:</PreviewLabel>
            <PreviewValue>{totalLabel}</PreviewValue>
          </FlexMiddle>
          {hourlyWage > 0 && (
            <PreviewLabel>
              {t('employeeLeave_expectedPay_breakdown', {
                days: dayCount,
                hours: dayHours.toFixed(1),
                rate: rateLabel
              })}
            </PreviewLabel>
          )}
          <ExcludedDaysNote count={excludedDayCount} />
        </FlexColumn>
      )}
    </PreviewBox>
  );
};

const CreateLeaveRecordModal = ({
  onClose,
  createLeaveRecord,
  management,
  fetchExpectedPay,
  employeeBranches = [],
  preselectedBalance,
  employeeName = '',
  firstName = ''
}) => {
  const { t, shortDateFormat } = useContext(ConfigContext);
  const { branchesOptions } = useBranches();
  const formattingMap = useFormattingMap();

  const balances = useMemo(() => management?.balances || [], [management]);
  const leaveOptions = useMemo(
    () =>
      balances.map((b) => ({
        label: b.leaveTypeName || `#${b.accountLeaveTypeId}`,
        value: b.accountContractV2LeaveId
      })),
    [balances]
  );

  const branchPickerOptions = useMemo(() => {
    // Fall back to all account branches when caller didn't pass per-employee ones.
    if (!employeeBranches?.length) return branchesOptions;
    const employeeBranchesSet = new Set(employeeBranches);
    return branchesOptions.filter(({ value }) => employeeBranchesSet.has(value));
  }, [branchesOptions, employeeBranches]);

  const initialValues = useMemo(() => {
    const values = {};
    if (preselectedBalance?.accountContractV2LeaveId) {
      values.accountContractV2LeaveId = preselectedBalance.accountContractV2LeaveId;
    }
    // Auto-select the branch when the employee belongs to only one.
    if (branchPickerOptions.length === 1) {
      values.branchId = branchPickerOptions[0].value;
    }
    return Object.keys(values).length ? values : undefined;
  }, [preselectedBalance, branchPickerOptions]);

  // Allow-negative leaves throw insufficient_funds_will_go_into_negative — the one error we confirm and retry.
  // insufficient_leave_balance / exceeds_negative_balance_cap are hard blocks (toast shown by the mutation).
  const [pendingNegative, setPendingNegative] = useState(null);
  const [pendingUnbook, setPendingUnbook] = useState(null);
  const [pendingEarningsLimit, setPendingEarningsLimit] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const submitCreate = useCallback(
    async (payload, { override = false, unbookConflictingShifts = false, overrideEarningsLimit = false } = {}) => {
      try {
        await createLeaveRecord({
          ...payload,
          overrideInsufficientBalance: override || undefined,
          unbookConflictingShifts: unbookConflictingShifts || undefined,
          overrideEarningsLimit: overrideEarningsLimit || undefined
        });
        onClose();
      } catch (error) {
        const data = error?.response?.data;
        const code = data?.errorCode;

        if (!override && code === LEAVE_ERROR_INSUFFICIENT_FUNDS_WILL_GO_NEGATIVE) {
          const selected = balances.find((b) => b.accountContractV2LeaveId === payload.accountContractV2LeaveId);
          setPendingNegative({
            payload,
            unbookConflictingShifts,
            overrideEarningsLimit,
            leaveTypeName: selected?.leaveTypeName || '',
            balance: data.params?.balance,
            expectedBalance: data.params?.expectedBalance,
            balanceUnit: data.params?.unit
          });
          return;
        }

        if (!unbookConflictingShifts && code === LEAVE_ERROR_EMPLOYEE_BOOKED_ON_DATES) {
          const selected = balances.find((b) => b.accountContractV2LeaveId === payload.accountContractV2LeaveId);
          const startMoment = moment(payload.startDate, API_DATE_FORMAT);
          const endMoment = moment(payload.endDate, API_DATE_FORMAT);
          const dateRange = startMoment.isSame(endMoment, 'day')
            ? startMoment.format(shortDateFormat)
            : [startMoment, endMoment]
                .filter((current) => current.isValid())
                .map((current) => current.format(shortDateFormat))
                .join(' - ');

          setPendingUnbook({
            payload,
            override,
            overrideEarningsLimit,
            shifts: data.params?.shifts || [],
            balance: data.params?.balance,
            expectedBalance: data.params?.expectedBalance,
            balanceUnit: data.params?.unit,
            leaveTypeName: selected?.leaveTypeName || '',
            dateRange
          });
          return;
        }

        if (!overrideEarningsLimit && code === LEAVE_ERROR_WILL_EXCEED_MONTHLY_EARNINGS_LIMIT) {
          setPendingEarningsLimit({ payload, override, unbookConflictingShifts });
        }
      }
    },
    [createLeaveRecord, onClose, balances, shortDateFormat]
  );

  const onSubmit = useCallback(
    async (values) => {
      const { dates, accountContractV2LeaveId, branchId, reason, durationHoursPerDay } = values;
      const startDate = dates?.startDate?.format?.(API_DATE_FORMAT);
      const endDate = dates?.endDate?.format?.(API_DATE_FORMAT);
      const parsedDuration = Number(durationHoursPerDay);
      const durationHours = Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : undefined;

      await submitCreate(
        { accountContractV2LeaveId, branchId, startDate, endDate, reason: reason || undefined, durationHours },
        { override: false }
      );
    },
    [submitCreate]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="smallSemiWideLong"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('createLeaveRecord')}
      confirmText={t('create')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <SelectField
          name="accountContractV2LeaveId"
          title={t('leave')}
          options={leaveOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
        />
        <SelectField
          name="branchId"
          title={t('branch')}
          options={branchPickerOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
        />
        <Label title={t('leaveDates')} required>
          <DateRangeField name="dates" validate={requiredDateRange} usePortal />
        </Label>
        <HoursPerDayField />
        <InputField name="reason" title={t('reason')} />
        <SelectedLeavePreview
          balances={balances}
          excludedHolidays={management?.excludedHolidays}
          fetchExpectedPay={fetchExpectedPay}
          formattingMap={formattingMap}
        />
      </Container>
      {pendingNegative && (
        <LeaveApprovalConfirmModal
          variant="negative"
          employeeName={employeeName}
          firstName={firstName}
          leaveTypeName={pendingNegative.leaveTypeName}
          balance={pendingNegative.balance}
          expectedBalance={pendingNegative.expectedBalance}
          balanceUnit={pendingNegative.balanceUnit}
          loading={confirming}
          onConfirm={async () => {
            if (confirming) return;
            const { payload, unbookConflictingShifts, overrideEarningsLimit } = pendingNegative;
            setConfirming(true);
            try {
              await submitCreate(payload, { override: true, unbookConflictingShifts, overrideEarningsLimit });
            } finally {
              setConfirming(false);
            }
            setPendingNegative(null);
          }}
          onClose={() => !confirming && setPendingNegative(null)}
        />
      )}
      {pendingUnbook && (
        <LeaveApprovalConfirmModal
          variant="unbook"
          employeeName={employeeName}
          firstName={firstName}
          leaveTypeName={pendingUnbook.leaveTypeName}
          dateRange={pendingUnbook.dateRange}
          shifts={pendingUnbook.shifts}
          balance={pendingUnbook.balance}
          expectedBalance={pendingUnbook.expectedBalance}
          balanceUnit={pendingUnbook.balanceUnit}
          loading={confirming}
          onConfirm={async () => {
            if (confirming) return;
            const { payload, override, overrideEarningsLimit } = pendingUnbook;
            setConfirming(true);
            try {
              await submitCreate(payload, { override, unbookConflictingShifts: true, overrideEarningsLimit });
            } finally {
              setConfirming(false);
            }
            setPendingUnbook(null);
          }}
          onClose={() => !confirming && setPendingUnbook(null)}
        />
      )}
      {pendingEarningsLimit && (
        <LeaveEarningsLimitWarningModal
          loading={confirming}
          onConfirm={async () => {
            if (confirming) return;
            const { payload, override, unbookConflictingShifts } = pendingEarningsLimit;
            setConfirming(true);
            try {
              await submitCreate(payload, { override, unbookConflictingShifts, overrideEarningsLimit: true });
            } finally {
              setConfirming(false);
            }
            setPendingEarningsLimit(null);
          }}
          onClose={() => !confirming && setPendingEarningsLimit(null)}
        />
      )}
    </FormModal>
  );
};

const HoursPerDayField = () => {
  const { t } = useContext(ConfigContext);
  return (
    <InputField
      name="durationHoursPerDay"
      title={t('createLeaveRecord_hoursPerDay')}
      type="number"
      placeholder={t('createLeaveRecord_hoursPerDayPlaceholder')}
    />
  );
};

const SelectedLeavePreview = ({ balances, excludedHolidays, fetchExpectedPay, formattingMap }) => {
  const { input: leaveInput } = useField('accountContractV2LeaveId', { subscription: { value: true } });
  const selected = balances.find((b) => b.accountContractV2LeaveId === leaveInput.value);

  if (isMoneyBalance(selected)) {
    return <MoneyPotPreview balance={selected} excludedHolidays={excludedHolidays} formattingMap={formattingMap} />;
  }
  return <ExpectedPayPreview fetchExpectedPay={fetchExpectedPay} formattingMap={formattingMap} />;
};

export default CreateLeaveRecordModal;
